import React, { useMemo, useEffect, useRef, useCallback, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L, { LatLngBoundsExpression } from "leaflet";
import { apiService } from "../../services/api";
import { TRANSPORT_COLORS, TRANSPORT_LINE_STYLES, TRANSPORT_META } from "./transportTheme";

interface RouteEdge {
  source: string;
  target: string;
  mode?: string;
  freight_mode?: string;
  waypoints?: { lat: number; lng: number }[];
  route_cost?: number;
  time_days?: number;
  distance_miles?: number;
}

export interface RouteData {
  route_id: string;
  rank?: number;
  edges: RouteEdge[];
  nodes?: string[];
  total_cost?: number;
  total_time?: number;
  total_distance?: number;
  _strategy_objective?: string;
}

interface WorldRouteMapProps {
  nodes: any[];
  edges?: RouteEdge[];
  /** Optional full route list. When omitted, the map renders only the route
   *  described by `edges`, which is the mode used by RouteAnalyzer. */
  allRoutes?: RouteData[];
  selectedRouteId?: string | null;
  hoveredRouteId?: string | null;
  onRouteClick?: (routeId: string) => void;
  onRouteHover?: (routeId: string | null) => void;
  /** When false, suppress the fallback polyline drawn between nodes when
   *  edges are empty. Default true. */
  showFallbackPolyline?: boolean;
}

interface Point {
  lat: number;
  lng: number;
  label: string;
}

type Waypoint = { lat: number; lng: number };

function getDashArrayForMode(mode: string): string {
  if (mode === "Air") return TRANSPORT_LINE_STYLES.Air.dashArray;
  if (mode === "Ocean") return TRANSPORT_LINE_STYLES.Ocean.dashArray;
  return TRANSPORT_LINE_STYLES.Ground.dashArray;
}

const AIR_CURVE_MAX_LAT = 85;
const AIR_CURVE_BASE_LIFT = 22;
const AIR_CURVE_SPAN_LIFT_FACTOR = 0.3;
const AIR_CURVE_POINTS = 56;

const waypointRequestCache = new Map<string, Promise<Waypoint[]>>();

const MODE_COLORS: Record<string, string> = {
  Ocean: TRANSPORT_COLORS.ocean,
  Air: TRANSPORT_COLORS.air,
  Ground: TRANSPORT_COLORS.ground,
};
const SELECTED_ROUTE_HALO = "#FFFFFF";

const makeDotIcon = (color: string) =>
  L.divIcon({
    className: "",
    html: `<div style="
      width:12px;height:12px;border-radius:50%;
      background:${color};border:2px solid #fff;
      box-shadow:0 0 4px rgba(0,0,0,0.4)"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

const originIcon = makeDotIcon("#22c55e");
const destinationIcon = makeDotIcon("#ef4444");
const intermediateIcon = makeDotIcon("#9ca3af");

function normalizeMode(edge: RouteEdge): string {
  const mode = (edge.mode || edge.freight_mode || "").toLowerCase();
  if (mode.includes("air") || mode.includes("flight") || mode.includes("plane")) return "Air";
  if (mode.includes("sea") || mode.includes("ocean") || mode.includes("ship") || mode.includes("vessel")) return "Ocean";
  if (mode.includes("ground") || mode.includes("truck") || mode.includes("rail") || mode.includes("road")) return "Ground";
  return "Ground";
}

function getWaypointRequestMode(mode: string): "Sea" | "Air" | "Ground" {
  if (mode === "Air") return "Air";
  if (mode === "Ground") return "Ground";
  return "Sea";
}

function normalizeLng(lng: number): number {
  while (lng > 180) lng -= 360;
  while (lng < -180) lng += 360;
  return lng;
}

function wrapLngNear(lng: number, center: number): number {
  while (lng - center > 180) lng -= 360;
  while (lng - center < -180) lng += 360;
  return lng;
}

function unwrapPositions(positions: [number, number][]): [number, number][] {
  if (positions.length < 2) return positions;
  const result: [number, number][] = [positions[0]];
  for (let i = 1; i < positions.length; i++) {
    let lng = positions[i][1];
    const prevLng = result[i - 1][1];
    if (lng - prevLng > 180) lng -= 360;
    else if (lng - prevLng < -180) lng += 360;
    result.push([positions[i][0], lng]);
  }
  return result;
}

function buildCappedAirCurve(
  start: [number, number],
  end: [number, number],
  pointCount: number = AIR_CURVE_POINTS
): [number, number][] {
  const [startLat, startLng] = start;
  const [endLat, rawEndLng] = end;
  const endLng = wrapLngNear(rawEndLng, startLng);
  const lngSpan = Math.abs(endLng - startLng);

  const maxEndpointLat = Math.max(startLat, endLat);
  const minPeakLat = maxEndpointLat + 6;
  const desiredPeakLat =
    maxEndpointLat + AIR_CURVE_BASE_LIFT + lngSpan * AIR_CURVE_SPAN_LIFT_FACTOR;
  const peakLat = Math.min(AIR_CURVE_MAX_LAT, Math.max(minPeakLat, desiredPeakLat));
  const controlLng = (startLng + endLng) / 2;

  const points: [number, number][] = [];
  for (let i = 0; i <= pointCount; i++) {
    const t = i / pointCount;
    const oneMinusT = 1 - t;
    const lat =
      oneMinusT * oneMinusT * startLat +
      2 * oneMinusT * t * peakLat +
      t * t * endLat;
    const lng =
      oneMinusT * oneMinusT * startLng +
      2 * oneMinusT * t * controlLng +
      t * t * endLng;
    points.push([lat, lng]);
  }

  return unwrapPositions(points);
}

function unwrapRouteSegments(
  segments: { positions: [number, number][]; mode: string }[]
): { positions: [number, number][]; mode: string }[] {
  if (segments.length <= 1) return segments;
  const result = [segments[0]];
  for (let i = 1; i < segments.length; i++) {
    const prevSeg = result[i - 1];
    const prevEnd = prevSeg.positions.at(-1)![1];
    const currStart = segments[i].positions[0][1];
    let shift = 0;
    const d = currStart - prevEnd;
    if (d > 180) shift = -360;
    else if (d < -180) shift = 360;
    if (shift === 0) {
      result.push(segments[i]);
    } else {
      result.push({
        ...segments[i],
        positions: segments[i].positions.map(
          ([lat, lng]) => [lat, lng + shift] as [number, number]
        ),
      });
    }
  }
  return result;
}

function alignRoutesToCommonWorld(
  routes: ProcessedRoute[]
): ProcessedRoute[] {
  if (routes.length <= 1) return routes;
  const refLngs = routes[0].segments.flatMap(s => s.positions.map(p => p[1]));
  if (refLngs.length === 0) return routes;
  const refCenter = (Math.min(...refLngs) + Math.max(...refLngs)) / 2;
  return routes.map((route, idx) => {
    if (idx === 0) return route;
    const lngs = route.segments.flatMap(s => s.positions.map(p => p[1]));
    if (lngs.length === 0) return route;
    const routeCenter = (Math.min(...lngs) + Math.max(...lngs)) / 2;
    const diff = routeCenter - refCenter;
    let shift = 0;
    if (diff > 180) shift = -360;
    else if (diff < -180) shift = 360;
    if (shift === 0) return route;
    return {
      ...route,
      segments: route.segments.map(seg => ({
        ...seg,
        positions: seg.positions.map(
          ([lat, lng]) => [lat, lng + shift] as [number, number]
        ),
      })),
    };
  });
}

const MapResizeHandler: React.FC = () => {
  const map = useMap();
  const resizeRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    const container = map.getContainer();
    resizeRef.current = new ResizeObserver(() => {
      map.invalidateSize();
    });
    resizeRef.current.observe(container);
    return () => {
      resizeRef.current?.disconnect();
    };
  }, [map]);

  return null;
};

const FitBounds: React.FC<{ bounds?: LatLngBoundsExpression }> = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      const [[minLat, minLng], [maxLat, maxLng]] =
        bounds as [[number, number], [number, number]];
      const lngSpan = Math.abs(maxLng - minLng);
      const latSpan = Math.abs(maxLat - minLat);
      const isLongRoute = lngSpan >= 140 || latSpan >= 60;

      map.invalidateSize();
      setTimeout(() => {
        map.fitBounds(bounds, {
          padding: isLongRoute ? [90, 90] : [50, 50],
          maxZoom: isLongRoute ? 4 : 8,
        });
      }, 100);
    }
  }, [bounds, map]);
  return null;
};

const MapBoundsController: React.FC<{ centerLng: number; hasRoutes: boolean }> = ({ centerLng, hasRoutes }) => {
  const map = useMap();
  useEffect(() => {
    if (hasRoutes) {
      map.setMaxBounds([[-85, centerLng - 200], [85, centerLng + 200]]);
    } else {
      map.setMaxBounds([[-85, -Infinity], [85, Infinity]]);
    }
  }, [map, centerLng, hasRoutes]);
  return null;
};

const ClickCatcher: React.FC<{ onMapClick: () => void }> = ({ onMapClick }) => {
  useMapEvents({ click: onMapClick });
  return null;
};

interface ProcessedRoute {
  routeId: string;
  rank: number;
  segments: {
    positions: [number, number][];
    mode: string;
    source?: string;
    target?: string;
    routeCost?: number;
    timeDays?: number;
    distanceMiles?: number;
  }[];
  totalCost?: number;
  totalTime?: number;
  totalDistance?: number;
  strategyObjective?: string;
  supplierName?: string;
  avgProbOnTime?: number;
  /** From backend-attached lead-time percentiles (after /lead-time attach) */
  medianLeadTimeDays?: number;
  p10LeadTimeDays?: number;
  p90LeadTimeDays?: number;
}

type NodeLookup = Record<string, { lat: number; lng: number }>;

function toLatLngTuple(wp: { lat: number; lng: number }): [number, number] {
  return [wp.lat, wp.lng];
}

async function fetchWaypoints(
  source: { lat: number; lng: number },
  target: { lat: number; lng: number },
  mode: string
): Promise<Waypoint[]> {
  const cacheKey = [
    source.lat.toFixed(6),
    source.lng.toFixed(6),
    target.lat.toFixed(6),
    target.lng.toFixed(6),
    mode.toLowerCase(),
  ].join("|");

  const cached = waypointRequestCache.get(cacheKey);
  if (cached) return cached;

  const requestPromise = (async () => {
    try {
      const res = await apiService.getRouteWaypoints(source, target, mode);
      return res.data.waypoints;
    } catch {
      return [source, target];
    }
  })();

  waypointRequestCache.set(cacheKey, requestPromise);
  return requestPromise;
}

function resolveSegmentPositions(
  waypoints: { lat: number; lng: number }[],
  mode: string
): [number, number][] {
  const rawPositions = unwrapPositions(waypoints.map(toLatLngTuple));
  const maxRawLat = Math.max(...rawPositions.map(([lat]) => lat));
  if (mode === "Air" && maxRawLat > AIR_CURVE_MAX_LAT && rawPositions.length >= 2) {
    return buildCappedAirCurve(rawPositions.at(0)!, rawPositions.at(-1)!);
  }
  return rawPositions;
}

function buildSegmentResult(
  edge: RouteEdge,
  mode: string,
  positions: Array<[number, number]>
): ProcessedRoute["segments"][number] {
  return {
    positions,
    mode,
    source: edge.source,
    target: edge.target,
    routeCost: typeof edge.route_cost === "number" ? edge.route_cost : undefined,
    timeDays: typeof edge.time_days === "number" ? edge.time_days : undefined,
    distanceMiles: typeof edge.distance_miles === "number" ? edge.distance_miles : undefined,
  };
}

async function buildSegmentFromEdgeAsync(
  edge: RouteEdge,
  nodeLookup: NodeLookup
): Promise<ProcessedRoute["segments"][number] | null> {
  const mode = normalizeMode(edge);

  // Use pre-provided waypoints when available
  if (edge.waypoints && edge.waypoints.length >= 2) {
    return buildSegmentResult(edge, mode, resolveSegmentPositions(edge.waypoints, mode));
  }

  const sourceCoords = nodeLookup[edge.source];
  const targetCoords = nodeLookup[edge.target];
  if (!sourceCoords || !targetCoords) return null;

  const waypoints = await fetchWaypoints(
    { lat: sourceCoords.lat, lng: sourceCoords.lng },
    { lat: targetCoords.lat, lng: targetCoords.lng },
    getWaypointRequestMode(mode)
  );
  return buildSegmentResult(edge, mode, resolveSegmentPositions(waypoints, mode));
}

async function buildSegmentsFromEdgesAsync(
  routeEdges: RouteEdge[],
  nodeLookup: NodeLookup
): Promise<ProcessedRoute["segments"]> {
  const promises = routeEdges.map(edge => buildSegmentFromEdgeAsync(edge, nodeLookup));
  const results = await Promise.all(promises);
  const segments: ProcessedRoute["segments"] = [];
  for (const seg of results) {
    if (seg) segments.push(seg);
  }
  return unwrapRouteSegments(segments);
}

function computeRouteCenterLng(processedRoutes: ProcessedRoute[]): number {
  const allLngs: number[] = [];
  for (const route of processedRoutes) {
    for (const seg of route.segments) {
      for (const p of seg.positions) {
        allLngs.push(p[1]);
      }
    }
  }

  if (allLngs.length === 0) return 0;
  return (Math.min(...allLngs) + Math.max(...allLngs)) / 2;
}

const formatTooltipCurrency = (value: number) =>
  `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const formatTooltipRisk = (avgProbOnTime?: number) =>
  avgProbOnTime == null ? null : `${Math.round((1 - avgProbOnTime) * 100)}%`;

const formatTooltipDistance = (value: number) =>
  `${Math.round(value).toLocaleString()} mi`;

const WorldRouteMap: React.FC<WorldRouteMapProps> = ({
  nodes = [],
  edges = [],
  allRoutes,
  selectedRouteId,
  hoveredRouteId,
  onRouteClick,
  onRouteHover,
  showFallbackPolyline = true,
}) => {
  const nodeLookup = useMemo(() => {
    const lookup: NodeLookup = {};
    nodes.forEach((n: any) => {
      const name = n.node_name;
      const lat = Number(n.latitude ?? n.lat);
      const lng = Number(n.longitude ?? n.lng);
      if (name && Number.isFinite(lat) && Number.isFinite(lng)) {
        lookup[name] = { lat, lng };
      }
    });
    return lookup;
  }, [nodes]);

  const nodePoints = useMemo<Point[]>(() => {
    if (!nodes.length) return [];
    return nodes
      .map((n: any) => {
        const lat = Number(n.latitude ?? n.lat);
        const lng = Number(n.longitude ?? n.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        const label =
          n.node_name ||
          (n.city && n.country ? `${n.city}, ${n.country}` : n.city) ||
          `Node ${n.node_id ?? ""}`;
        return { lat, lng, label };
      })
      .filter((p): p is Point => p !== null);
  }, [nodes]);

  const [processedRoutes, setProcessedRoutes] = useState<ProcessedRoute[]>([]);
  const resolveRunIdRef = useRef(0);
  const hasRouteList = Boolean(allRoutes?.length);
  const isSingleRouteInput = !hasRouteList;

  async function handleNoRouteList(runId: number) {
    const isStale = () => runId !== resolveRunIdRef.current;
    if (!edges.length || !Object.keys(nodeLookup).length) {
      // Draw a fallback polyline between nodes only when allowed
      if (showFallbackPolyline && nodePoints.length >= 2) {
        const positions = nodePoints.map(p => [p.lat, p.lng] as [number, number]);
        if (!isStale()) {
          setProcessedRoutes([{
            routeId: "default",
            rank: 1,
            segments: [{ positions, mode: "default" }],
          }]);
        }
        return;
      }
      if (!isStale()) setProcessedRoutes([]);
      return;
    }

    const segments = await buildSegmentsFromEdgesAsync(edges, nodeLookup);
    if (!isStale()) {
      setProcessedRoutes([{ routeId: "single", rank: 1, segments }]);
    }
  }

  async function handleRouteList(list: RouteData[], runId: number) {
    const isStale = () => runId !== resolveRunIdRef.current;
    const rawRoutes: ProcessedRoute[] = [];
    for (let idx = 0; idx < list.length; idx++) {
      const route = list[idx];
      const segments = await buildSegmentsFromEdgesAsync(route.edges || [], nodeLookup);
      if (isStale()) {
        return;
      }

      const rAny = route as any;
      rawRoutes.push({
        routeId: route.route_id || `route-${idx}`,
        rank: route.rank ?? idx + 1,
        segments,
        totalCost: route.total_cost,
        totalTime: route.total_time,
        totalDistance: route.total_distance,
        strategyObjective: route._strategy_objective,
        supplierName: rAny.supplier_name,
        avgProbOnTime: rAny.avg_prob_on_time,
        medianLeadTimeDays:
          rAny.median_lead_time_days == null ? undefined : Number(rAny.median_lead_time_days),
        p10LeadTimeDays:
          rAny.p10_lead_time_days == null ? undefined : Number(rAny.p10_lead_time_days),
        p90LeadTimeDays:
          rAny.p90_lead_time_days == null ? undefined : Number(rAny.p90_lead_time_days),
      });
    }
    if (!isStale()) {
      setProcessedRoutes(alignRoutesToCommonWorld(rawRoutes));
    }
  }

  useEffect(() => {
    const runId = ++resolveRunIdRef.current;

    async function resolve() {
      if (!allRoutes?.length) {
        await handleNoRouteList(runId);
        return;
      }

      await handleRouteList(allRoutes, runId);
    }

    resolve();
    return () => {
      // Invalidate this run so stale async results can't overwrite newer routes.
      if (resolveRunIdRef.current === runId) {
        resolveRunIdRef.current += 1;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRoutes, edges, hasRouteList, nodeLookup, nodePoints, showFallbackPolyline]);

  const routeCenterLng = useMemo(() => computeRouteCenterLng(processedRoutes), [processedRoutes]);

  const bounds = useMemo<LatLngBoundsExpression | undefined>(() => {
    const allPositions: [number, number][] = [];

    if (selectedRouteId && processedRoutes.length > 0) {
      const selected = processedRoutes.find(r => r.routeId === selectedRouteId);
      if (selected) {
        selected.segments.forEach(seg => {
          allPositions.push(...seg.positions);
        });
      }
    }

    if (allPositions.length === 0) {
      processedRoutes.forEach(route => {
        route.segments.forEach(seg => {
          allPositions.push(...seg.positions);
        });
      });
    }

    if (allPositions.length === 0 && nodePoints.length > 0) {
      nodePoints.forEach(p => allPositions.push([p.lat, wrapLngNear(p.lng, routeCenterLng)]));
    }

    if (allPositions.length === 0) return undefined;

    const lats = allPositions.map(p => p[0]);
    const lngs = allPositions.map(p => p[1]);
    const pad = 3;

    const minLat = Math.max(Math.min(...lats) - pad, -85);
    const maxLat = Math.min(Math.max(...lats) + pad, 85);
    const minLng = Math.min(...lngs) - pad;
    const maxLng = Math.max(...lngs) + pad;

    if (maxLng - minLng > 300) {
      return [[-60, -180], [70, 180]];
    }

    return [[minLat, minLng], [maxLat, maxLng]];
  }, [processedRoutes, nodePoints, selectedRouteId, routeCenterLng]);

  const getRouteStyle = useCallback((routeId: string, _rank: number, mode: string) => {
    const isSelected = selectedRouteId === routeId || (isSingleRouteInput && processedRoutes.length === 1);
    const isHovered = hoveredRouteId === routeId;

    const baseColor = MODE_COLORS[mode] || "#1976d2";
    const dashArray = getDashArrayForMode(mode);

    if (isSelected || isHovered) {
      return {
        color: baseColor,
        weight: isHovered ? 7 : 6,
        opacity: 1,
        dashArray,
      };
    }

    if (selectedRouteId && hasRouteList) {
      return {
        color: baseColor,
        weight: 2,
        opacity: 0.35,
        dashArray,
      };
    }

    return {
      color: baseColor,
      weight: 3,
      opacity: 0.8,
      dashArray,
    };
  }, [selectedRouteId, hoveredRouteId, hasRouteList, isSingleRouteInput, processedRoutes.length]);

  const getHaloStyle = useCallback((routeId: string, mode: string) => {
    const isSelected = selectedRouteId === routeId || (isSingleRouteInput && processedRoutes.length === 1);
    if (!isSelected) return null;

    const dashArray = getDashArrayForMode(mode);

    return {
      color: SELECTED_ROUTE_HALO,
      weight: 10,
      opacity: 0.92,
      dashArray,
      lineCap: "round" as const,
      lineJoin: "round" as const,
    };
  }, [selectedRouteId, isSingleRouteInput, processedRoutes.length]);

  const handleMapClick = useCallback(() => {
    // no-op
  }, []);

  const sortedRoutes = useMemo(() => {
    if (!hasRouteList || processedRoutes.length <= 1) return processedRoutes;
    return [...processedRoutes].sort((a, b) => {
      const aSelected = a.routeId === selectedRouteId;
      const bSelected = b.routeId === selectedRouteId;
      if (aSelected) return 1;
      if (bSelected) return -1;
      return 0;
    });
  }, [processedRoutes, selectedRouteId, hasRouteList]);

  const markerPositions = useMemo(() => {
    const routeEndpoints: [number, number][] = [];
    processedRoutes.forEach(route => {
      route.segments.forEach(seg => {
        if (seg.positions.length > 0) {
          const lastPos = seg.positions[seg.positions.length - 1];
          routeEndpoints.push(seg.positions[0], lastPos);
        }
      });
    });

    const results: { lat: number; lng: number; label: string; icon: L.DivIcon; key: string }[] = [];

    nodePoints.forEach((pt, idx) => {
      const isOrigin = idx === 0;
      const isDestination = idx === nodePoints.length - 1;
      let icon = intermediateIcon;
      if (isOrigin) {
        icon = originIcon;
      } else if (isDestination) {
        icon = destinationIcon;
      }

      let bestLng = wrapLngNear(pt.lng, routeCenterLng);
      let found = false;
      const nPt = normalizeLng(pt.lng);
      for (const [epLat, epLng] of routeEndpoints) {
        if (Math.abs(epLat - pt.lat) < 2) {
          const nEp = normalizeLng(epLng);
          if (Math.abs(nEp - nPt) < 2) {
            if (!found || epLng > bestLng) {
              bestLng = epLng;
              found = true;
            }
          }
        }
      }

      results.push({ lat: pt.lat, lng: bestLng, label: pt.label, icon, key: `${pt.label}-${idx}` });
    });

    return results;
  }, [nodePoints, processedRoutes, routeCenterLng]);

  return (
    <div style={{ height: "100%", width: "100%", overflow: "hidden", position: "relative" }}>
      <MapContainer
        center={[20, routeCenterLng]}
        zoom={2}
        minZoom={1.5}
        zoomSnap={0.5}
        zoomDelta={0.5}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />
        <MapResizeHandler />
        <MapBoundsController centerLng={routeCenterLng} hasRoutes={processedRoutes.length > 0} />
        {bounds && <FitBounds bounds={bounds} />}
        <ClickCatcher onMapClick={handleMapClick} />

        {/* Render polylines only when showFallbackPolyline is true */}
        {showFallbackPolyline && sortedRoutes.map((route) => (
          <React.Fragment key={route.routeId}>
            {route.segments.map((segment, segIdx) => {
              const style = getRouteStyle(route.routeId, route.rank, segment.mode);
              let modeMeta;
              if (segment.mode === "Air") {
                modeMeta = TRANSPORT_META.Air;
              } else if (segment.mode === "Ocean") {
                modeMeta = TRANSPORT_META.Ocean;
              } else {
                modeMeta = TRANSPORT_META.Ground;
              }
              const legLabel =
                segment.source && segment.target
                  ? `${segment.source} → ${segment.target}`
                  : "Route segment";
              let riskColor = "#ef4444";
              if (route.avgProbOnTime != null) {
                if (route.avgProbOnTime >= 0.8) {
                  riskColor = "#10b981";
                } else if (route.avgProbOnTime >= 0.6) {
                  riskColor = "#f59e0b";
                }
              }
              return (
                <React.Fragment key={`${route.routeId}-${segIdx}`}>
                  {getHaloStyle(route.routeId, segment.mode) && (
                    <Polyline
                      positions={segment.positions}
                      pathOptions={getHaloStyle(route.routeId, segment.mode)!}
                      interactive={false}
                    />
                  )}
                  <Polyline
                    positions={segment.positions}
                    pathOptions={style}
                    eventHandlers={{
                      click: (e) => {
                        L.DomEvent.stopPropagation(e.originalEvent);
                        onRouteClick?.(route.routeId);
                      },
                      mouseover: () => onRouteHover?.(route.routeId),
                      mouseout: () => onRouteHover?.(null),
                    }}
                  >
                    <Tooltip sticky>
                      <div
                        style={{
                          minWidth: 260,
                          maxWidth: 340,
                          background: "rgba(255,255,255,0.98)",
                          border: "1px solid rgba(15, 23, 42, 0.08)",
                          borderRadius: 12,
                          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.16)",
                          padding: 12,
                          lineHeight: 1.4,
                          fontSize: 12,
                          color: "#0F172A",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 13 }}>{legLabel}</div>
                          </div>
                          <div
                            style={{
                              flexShrink: 0,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "4px 8px",
                              borderRadius: 999,
                              background: modeMeta.bgColor,
                              color: modeMeta.color,
                              fontWeight: 700,
                              fontSize: 11,
                            }}
                          >
                            <span
                              style={{
                                width: 14,
                                borderTop: `2px solid ${modeMeta.color}`,
                                borderTopStyle: segment.mode === "Air" ? "dashed" : "solid",
                                display: "inline-block",
                              }}
                            />
                            {segment.mode}
                          </div>
                        </div>

                        {route.supplierName && (
                          <div style={{ fontSize: 11, color: "#475569", marginBottom: 10 }}>
                            Supplier: <span style={{ fontWeight: 600, color: "#0F172A" }}>{route.supplierName}</span>
                          </div>
                        )}

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                            gap: 12,
                            paddingTop: 10,
                            borderTop: "1px solid #E2E8F0",
                          }}
                        >
                          {segment.routeCost != null && (
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: "#94A3B8", marginBottom: 3, whiteSpace: "nowrap" }}>Cost</div>
                              <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>{formatTooltipCurrency(segment.routeCost)}</div>
                            </div>
                          )}
                          {segment.timeDays != null && (
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: "#94A3B8", marginBottom: 3, whiteSpace: "nowrap" }}>Transit Time</div>
                              <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>{segment.timeDays.toFixed(1)} days</div>
                            </div>
                          )}
                          {segment.distanceMiles != null && (
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: "#94A3B8", marginBottom: 3, whiteSpace: "nowrap" }}>Distance</div>
                              <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>{formatTooltipDistance(segment.distanceMiles)}</div>
                            </div>
                          )}
                        </div>
                        {route.avgProbOnTime != null && (
                          <div style={{ fontSize: 11, color: "#64748B", marginTop: 10 }}>
                            Route risk: <span style={{ fontWeight: 700, color: riskColor }}>{formatTooltipRisk(route.avgProbOnTime)}</span>
                          </div>
                        )}
                      </div>
                    </Tooltip>
                  </Polyline>
                </React.Fragment>
              );
            })}
          </React.Fragment>
        ))}

        {markerPositions.map((mp) => (
          <Marker key={mp.key} position={[mp.lat, mp.lng]} icon={mp.icon}>
            <Tooltip direction="top" offset={[0, -10]} permanent>
              {mp.label}
            </Tooltip>
          </Marker>
        ))}
      </MapContainer>

    </div>
  );
};

export default WorldRouteMap;
